import type { FolderId, GroupId, GroupMemberInvitationId, WorkspaceId } from 'balus-common';
import { ViewModelAssetURL } from './urls';
import { Folder } from '@workspace/view-model-folder/domain/Folder';

export class UserPagePathBuilder {
  /**
   * ログインページ
   */
  static loginPage(requestPath?: string): string {
    const path = '/login';
    if (requestPath) {
      const params = new URLSearchParams();
      params.set('requestPath', requestPath);
      return `${path}?${params.toString()}`;
    }
    return path;
  }

  /**
   * Firebase 認証によるユーザ登録ページ(pendingRegistration の受け皿)
   */
  static firebaseSignUpPage(): string {
    return '/user/signUpWithFirebaseAuth';
  }

  /**
   * ログアウトページ
   */
  static logoutPage(): string {
    return '/logout';
  }

  /**
   * エントリーページ
   */
  static entryPage(): string {
    return '/?mode=entry';
  }

  /**
   * パスワード再設定ページ
   */
  static passwordResetPage(): string {
    return '/user/passwordReset';
  }

  /**
   * メールアドレス変更の確認ページ(確認メール内リンクの遷移先)
   */
  static mailAddressChangeActionPage(): string {
    return '/user/mailAddressChangeAction';
  }

  /**
   * サインアップ要求完了ページ
   */
  static signUpRequestDonePage(): string {
    return '/user/signUpRequestDone';
  }

  /**
   * ユーザ登録確認ページ（本登録）
   */
  static signUpRequestConfirmPage(): string {
    return '/user/signUpRequestConfirm';
  }

  /**
   * 既存ユーザに対するアカウント連携完了の案内ページ
   */
  static accountConnectedPage(): string {
    return '/user/accountConnected';
  }

  /**
   * メール配信停止要求ページ
   */
  static stopMailRequestPage(): string {
    return '/user/stopMailRequest';
  }

  static groupPage(groupId: string): string {
    return `/groups/${groupId}`;
  }

  static groupConsolePage(groupId: string): string {
    return `/groups/${groupId}/console`;
  }

  static groupConsoleLicensePage(groupId: string): string {
    return `${this.groupConsolePage(groupId)}/license`;
  }

  static groupConsoleMembersPage(groupId: string): string {
    return `${this.groupConsolePage(groupId)}/members`;
  }

  static groupConsoleMemberPage(groupId: string, memberId: string): string {
    return `${this.groupConsolePage(groupId)}/members/${memberId}`;
  }

  static groupConsoleInvitationsPage(groupId: string): string {
    return `${this.groupConsolePage(groupId)}/invitations`;
  }

  static groupConsoleIpRestrictionPage(groupId: string): string {
    return `${this.groupConsolePage(groupId)}/ip-restriction`;
  }

  static groupConsoleInvitePage(groupId: string): string {
    return `${this.groupConsolePage(groupId)}/invite`;
  }

  static groupMemberInvitationPage(): string {
    return '/group-member-invitations';
  }

  static tagAssignedViewModelsPage(groupId: string, viewModelTagId: string): string {
    return `/groups/${groupId}/tag-assigned-view-models/${viewModelTagId}`;
  }

  /**
   * グループへのメンバー招待を承諾するURL
   * NOTE: Cloud Functions 側でもこのURLを組み立てている箇所があるので、変更する際には注意すること。
   */
  static buildGroupMemberInvitationUrl(
    groupId: GroupId,
    invitationId: GroupMemberInvitationId,
    baseUrl = window.location.href
  ): string {
    const url = new URL(this.groupMemberInvitationPage(), baseUrl);

    url.searchParams.set('groupId', groupId);
    url.searchParams.set('invitationId', invitationId);

    return url.toString();
  }

  static workspacePage(workspaceId: WorkspaceId): string {
    return `/workspaces/${workspaceId}`;
  }

  static workspaceFolderPage(workspaceId: WorkspaceId, folderId: FolderId): string {
    return folderId === Folder.ROOT_ID
      ? this.workspacePage(workspaceId)
      : `/workspaces/${workspaceId}/folders/${folderId}`;
  }

  /**
   * 古い形式のビューモデルURLパス。
   *
   */
  static legacyViewModelPage(workspaceId: string, viewModelId: string): string {
    return `/workspaces/${workspaceId}/view-models/${viewModelId}`;
  }

  /**
   * 新しい形式のビューモデルURLパス。
   *
   * @param viewModelId
   * @returns
   */
  static viewModelPage(viewModelId: string): string {
    return `/view-models/${viewModelId}`;
  }

  /**
   * ビューモデルに貼付されたmedia assetのパス
   * @param groupId
   * @param viewModelId
   * @param assetId
   * @returns
   */
  static viewModelAssetPath(groupId: string, viewModelId: string, assetId: string): string {
    return new ViewModelAssetURL(groupId, viewModelId, assetId).path;
  }

  /**
   * KATA MAPへのビューモデルURLを返す。
   *
   * @returns
   */
  static kataMapPage(source: 'sidebar' | 'menu'): string {
    return `https://levii.notion.site/KATA-Map-2496e6221d49487e9cd608f3dfcfd552?source=${source}`;
  }
}
